'use client';

import React from 'react';
import Link from 'next/link';
import { ProtectedAppImage } from '@/components/ProtectedImage/ProtectedAppImage';
import Header from '@/components/common/Header';
import Footer from '@/app/homepage/components/SiteFooter';
import { useAuth } from '@/contexts/AuthContext';
import { getAuthRedirectUrl } from '@/lib/redirectUtils';

const Check = ({ className = 'w-5 h-5' }: { className?: string }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 20 20">
    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
  </svg>
);

export default function VirtualStagingService() {
  const { isAuthenticated } = useAuth();
  const signupUrl = getAuthRedirectUrl(isAuthenticated);

  const styles = [
    { name: 'Modern', emoji: '⚫', desc: 'Clean lines & minimalist elegance' },
    { name: 'Luxury', emoji: '👑', desc: 'High-end furnishings & premium touches' },
    { name: 'Cozy', emoji: '🛋️', desc: 'Warm, inviting, and comfortable spaces' },
    { name: 'Contemporary', emoji: '🎨', desc: 'Trendy, artistic, and vibrant designs' },
    { name: 'Traditional', emoji: '🕯️', desc: 'Classic elegance with timeless appeal' },
    { name: 'Farmhouse', emoji: '🌾', desc: 'Rustic charm with modern comfort' },
    { name: 'Urban', emoji: '🏙️', desc: 'Industrial chic with modern flair' },
    { name: 'Scandinavian', emoji: '❄️', desc: 'Light, airy, and functional design' },
  ];

  const whyVirtualStaging = [
    { title: 'Increase Buyer Interest', description: "90% of buyers start looking online, where you only have a few moments to catch a buyer's interest.", icon: '👥', metric: '90%' },
    { title: 'Sell Properties Faster', description: 'Homes that are staged sell 75% faster than those that are not.', icon: '⚡', metric: '75%' },
    { title: 'Higher Sale Price', description: '83% of staged properties sell for the asking price or above.', icon: '💰', metric: '83%' },
    { title: 'A Fraction of the Cost', description: 'We can virtually furnish an entire room for just US$24.00 per image - a fraction of traditional staging costs.', icon: '💵', metric: '$24' },
    { title: 'Get Rid of That Empty Feeling', description: 'Fill your listing with stylish furniture which allows buyers to visualize themselves in the home.', icon: '🏠', metric: '100%' },
    { title: 'Stage It to Suit', description: 'Select from our range of style options to stage your listing to suit the home perfectly.', icon: '🎨', metric: '8+' },
  ];

  const benefits = [
    { metric: '90%', label: 'Online Buyers', description: 'Start their search online - first impressions matter' },
    { metric: '75%', label: 'Faster Sales', description: 'Staged homes sell significantly faster' },
    { metric: '83%', label: 'At/Above Asking', description: 'Staged properties meet or exceed asking price' },
    { metric: '$24', label: 'Per Image', description: 'Affordable professional virtual staging' },
  ];

  const beforeAfterExamples = [
    { before: '/images/gallery/3vsbf.webp?auto=compress&cs=tinysrgb&w=600', after: '/images/gallery/3vsaf.webp?auto=compress&cs=tinysrgb&w=600', title: 'Living Room Staging', turnaround: '48 Hours' },
    { before: '/images/gallery/2vsbf.webp?auto=compress&cs=tinysrgb&w=600', after: '/images/gallery/2vsaf.webp?auto=compress&cs=tinysrgb&w=600', title: 'Bedroom Transformation', turnaround: '48 Hours' },
    { before: '/images/gallery/ondorevsbefore.webp?auto=compress&cs=tinysrgb&w=600', after: '/images/gallery/ondorevsafter.webp?auto=compress&cs=tinysrgb&w=600', title: 'Dining Room Staging', turnaround: '48 Hours' },
  ];

  const processSteps = [
    { n: '1', title: 'Upload Your Photos', desc: 'Send us clear photos of the empty spaces you want to stage. Make sure the room is well-lit and clutter-free for best results.' },
    { n: '2', title: 'Choose Your Style', desc: 'Select from Modern, Luxury, Cozy, Contemporary, Traditional, Farmhouse, Urban, or Scandinavian styles to match your property.' },
    { n: '3', title: 'Expert Staging', desc: 'Our team carefully selects and places furniture that matches your space perfectly, with realistic lighting, shadows, and perfect scaling.' },
    { n: '4', title: 'Download & Use', desc: 'Get high-resolution images ready for your listings in 48 hours. Unlimited revisions included at no extra cost!' },
  ];

  const features = [
    { icon: '📐', title: 'Perfectly Scaled', desc: 'Furniture sized exactly to your room dimensions using digital measurement' },
    { icon: '💡', title: 'Smart Lighting', desc: 'Realistic shadows, reflections, and ambient lighting for photo-realistic results' },
    { icon: '🎨', title: 'Color Matched', desc: 'Furniture colors and styles that complement your room perfectly' },
    { icon: '🔄', title: 'Unlimited Revisions', desc: 'Get it exactly right with unlimited changes at no extra cost' },
    { icon: '⚡', title: '48-Hour Delivery', desc: 'Fast turnaround without compromising on quality' },
    { icon: '🎯', title: 'Custom Requests', desc: 'Send sample images of furniture you like for closest possible match' },
    { icon: '🏆', title: 'Expert Editors', desc: 'Professional team with years of virtual staging experience' },
    { icon: '💰', title: 'No Subscriptions', desc: 'Pay as you go - no hidden fees or monthly commitments' },
  ];

  const dotGrid = `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`;

  const Badge = ({ children }: { children: React.ReactNode }) => (
    <span className="inline-flex items-center bg-white/[0.04] border border-white/[0.12] text-purple-200 px-6 py-2 rounded-full text-sm font-bold">
      {children}
    </span>
  );

  return (
    <div className="bg-[#0B0D17] text-white">
      <Header />

      {/* ===================== HERO ===================== */}
      <section className="relative overflow-hidden bg-[#0B0D17] border-b border-white/[0.06]">
        <div className="absolute inset-0">
          <div className="absolute top-1/4 left-1/4 w-[1000px] h-[1000px] bg-purple-600/10 rounded-full blur-[160px] animate-glow-breathe" />
          <div className="absolute bottom-1/4 right-1/4 w-[800px] h-[800px] bg-blue-600/5 rounded-full blur-[140px] animate-glow-breathe-delayed" />
          <div className="absolute inset-0 opacity-[0.025]" style={{ backgroundImage: dotGrid }} />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 py-20 lg:py-28">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/[0.04] border border-white/[0.12] backdrop-blur-sm text-purple-200 text-xs font-medium">
                <span className="text-sm">⚡</span>
                <span>Get started in under 60 seconds</span>
              </div>

              <div className="space-y-6">
                <h1 className="text-5xl lg:text-7xl font-bold leading-[1.05] tracking-tight">
                  Virtual staging{' '}
                  <span className="bg-gradient-to-r from-purple-400 via-purple-300 to-blue-400 bg-clip-text text-transparent">
                    that sells.
                  </span>
                </h1>
                <p className="text-xl text-gray-400 leading-relaxed">
                  Transform empty spaces into inviting homes. Our photo-realistic furniture staging makes buyers see
                  the potential instantly — only US$24.00 per image with no subscriptions.
                </p>
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <Link href={signupUrl}>
                  <button className="group w-full sm:w-auto px-9 py-4 bg-gradient-to-r from-purple-600 to-purple-500 hover:from-purple-500 hover:to-purple-400 text-white rounded-xl font-bold text-lg transition-all duration-300 flex items-center justify-center gap-2 shadow-xl shadow-purple-500/30 hover:shadow-purple-500/50 hover:-translate-y-0.5">
                    Start Staging Now
                    <svg className="w-5 h-5 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                    </svg>
                  </button>
                </Link>
                <Link href="#process">
                  <button className="w-full sm:w-auto px-9 py-4 bg-white/[0.03] hover:bg-white/[0.08] backdrop-blur-sm border border-white/[0.15] hover:border-white/30 text-white rounded-xl font-bold text-lg transition-all duration-300">
                    See How It Works
                  </button>
                </Link>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 pt-4">
                {benefits.map((benefit, i) => (
                  <div key={i} className="text-center bg-white/[0.03] border border-white/[0.08] rounded-xl p-4 hover:border-purple-500/30 transition-all duration-300">
                    <div className="text-3xl lg:text-4xl font-bold bg-gradient-to-r from-purple-400 to-blue-400 bg-clip-text text-transparent mb-1">{benefit.metric}</div>
                    <div className="text-xs font-semibold text-white mb-1">{benefit.label}</div>
                    <div className="text-xs text-gray-500 leading-tight">{benefit.description}</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="relative">
              <div className="absolute -inset-4 bg-purple-600/15 rounded-3xl blur-[80px] -z-10" />
              <div className="relative h-[450px] lg:h-[550px] rounded-2xl overflow-hidden shadow-2xl border-2 border-white/[0.12]">
                <ProtectedAppImage
                  src="/images/gallery/staging-living-after.webp?auto=compress&cs=tinysrgb&w=1200"
                  alt="Virtually staged living room"
                  className="w-full h-full object-cover"
                />
                <div className="absolute top-6 right-6 bg-gradient-to-r from-purple-600 to-purple-500 text-white px-5 py-3 rounded-lg font-bold text-sm shadow-lg shadow-purple-500/30">
                  VIRTUALLY STAGED
                </div>
                <div className="absolute bottom-6 left-6 bg-black/60 backdrop-blur-sm border border-white/10 text-white px-5 py-3 rounded-lg font-bold text-sm">
                  48 HOUR TURNAROUND
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===================== WHY VIRTUAL STAGING ===================== */}
      <section className="relative py-20 lg:py-28 px-6 lg:px-12">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16 max-w-3xl mx-auto">
            <h2 className="text-4xl lg:text-5xl font-bold leading-tight tracking-tight mb-4">
              Why fill your empty listing with virtual staging?
            </h2>
            <p className="text-lg text-gray-400 leading-relaxed">
              Virtual staging isn&apos;t just about making spaces look better — it&apos;s about driving real results and
              helping buyers fall in love with your property.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {whyVirtualStaging.map((item, index) => (
              <div key={index} className="group text-center bg-[#13151F]/80 backdrop-blur-sm border border-white/[0.08] rounded-2xl p-8 hover:border-purple-500/30 hover:-translate-y-1 transition-all duration-300">
                <div className="w-16 h-16 mx-auto rounded-2xl bg-gradient-to-br from-purple-500/20 to-blue-500/10 border border-purple-500/20 flex items-center justify-center text-3xl mb-4 group-hover:scale-110 transition-transform">
                  {item.icon}
                </div>
                <div className="text-3xl font-bold bg-gradient-to-r from-purple-400 to-blue-400 bg-clip-text text-transparent mb-2">{item.metric}</div>
                <h3 className="text-lg font-bold text-white mb-3">{item.title}</h3>
                <p className="text-gray-400 text-sm leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===================== PHOTO-REALISTIC QUALITY ===================== */}
      <section className="relative py-20 lg:py-28 px-6 lg:px-12 bg-white/[0.015] border-y border-white/[0.06]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16 max-w-3xl mx-auto">
            <div className="mb-6"><Badge>PHOTO-REALISTIC VIRTUAL STAGING</Badge></div>
            <h2 className="text-4xl lg:text-5xl font-bold leading-tight tracking-tight mb-4">
              No fake-looking furniture. No floating objects.
            </h2>
            <p className="text-lg text-gray-400 leading-relaxed">
              Afraid that virtual staging will look fake? Don&apos;t like floating furniture? Neither do we! We use
              cutting-edge technology combined with the latest staging techniques to ensure your finished image is
              photo-realistic to the eye.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-6">
            {[
              {
                icon: '💡',
                title: 'Advanced Lighting & Shadows',
                body: 'Techniques including advanced lighting, shadowing and micro textures are all seamlessly blended together so your finished image looks as if it was traditionally staged.',
                points: ['Realistic shadow casting', 'Natural light reflection', 'Ambient occlusion'],
              },
              {
                icon: '📐',
                title: 'Scaled to Perfection',
                body: 'Want to know an industry secret to make a virtually staged room look photo-realistic? Digital scaling. Our editors go the extra mile by digitally measuring your room so the added furniture fits perfectly to scale.',
                points: ['Precise room measurements', 'Furniture sized to scale', 'Perspective-perfect placement'],
              },
            ].map((card) => (
              <div key={card.title} className="bg-[#13151F]/80 backdrop-blur-sm rounded-2xl p-8 border border-white/[0.08] hover:border-purple-500/30 transition-all duration-300">
                <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-purple-500/20 to-blue-500/10 border border-purple-500/20 flex items-center justify-center text-2xl mb-5">{card.icon}</div>
                <h3 className="text-2xl font-bold text-white mb-4">{card.title}</h3>
                <p className="text-gray-400 leading-relaxed mb-4">{card.body}</p>
                <ul className="space-y-3">
                  {card.points.map((p) => (
                    <li key={p} className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-purple-400 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-300">{p}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===================== BEFORE / AFTER ===================== */}
      <section className="relative py-20 lg:py-28 px-6 lg:px-12">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16 max-w-2xl mx-auto">
            <h2 className="text-4xl lg:text-5xl font-bold leading-tight tracking-tight mb-4">See the transformation</h2>
            <p className="text-lg text-gray-400 leading-relaxed">
              Real before and after examples showcasing the power of professional virtual staging.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {beforeAfterExamples.map((example, index) => (
              <div key={index} className="bg-[#13151F]/80 backdrop-blur-sm rounded-2xl overflow-hidden border border-white/[0.08] hover:border-purple-500/30 transition-all duration-300">
                <div className="grid grid-cols-2 gap-0">
                  <div className="relative h-48">
                    <ProtectedAppImage src={example.before} alt={`${example.title} - Before`} className="w-full h-full object-cover" />
                    <div className="absolute top-3 left-3 bg-black/60 backdrop-blur-sm border border-white/10 text-white px-3 py-1 text-xs font-bold rounded">BEFORE</div>
                  </div>
                  <div className="relative h-48">
                    <ProtectedAppImage src={example.after} alt={`${example.title} - After`} className="w-full h-full object-cover" />
                    <div className="absolute top-3 right-3 bg-gradient-to-r from-purple-600 to-purple-500 text-white px-3 py-1 text-xs font-bold rounded shadow-lg shadow-purple-500/30">AFTER</div>
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="font-bold text-lg text-white mb-2">{example.title}</h3>
                  <div className="flex items-center gap-2 text-sm text-gray-400">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span>{example.turnaround}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===================== STYLE OPTIONS ===================== */}
      <section className="relative py-20 lg:py-28 px-6 lg:px-12 bg-white/[0.015] border-y border-white/[0.06]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16 max-w-2xl mx-auto">
            <h2 className="text-4xl lg:text-5xl font-bold leading-tight tracking-tight mb-4">Multiple furniture styles to choose from</h2>
            <p className="text-lg text-gray-400 leading-relaxed">
              From Modern to Traditional, Urban to Farmhouse and more! Our team carefully selects virtual furniture
              that best complements your listing. You can even send sample images and we&apos;ll match them closely.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {styles.map((style, i) => (
              <div key={i} className="group bg-[#13151F]/80 backdrop-blur-sm border border-white/[0.08] rounded-2xl p-8 hover:border-purple-500/30 hover:-translate-y-1 transition-all duration-300">
                <div className="text-5xl mb-4 group-hover:scale-110 transition-transform">{style.emoji}</div>
                <h3 className="text-2xl font-bold text-white mb-2">{style.name}</h3>
                <p className="text-gray-400 text-sm">{style.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===================== PRICING ===================== */}
      <section className="relative py-20 lg:py-28 px-6 lg:px-12">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16 max-w-3xl mx-auto">
            <div className="mb-6"><Badge>NO SUBSCRIPTIONS - PAY AS YOU GO</Badge></div>
            <h2 className="text-4xl lg:text-5xl font-bold leading-tight tracking-tight mb-4">Only US$24.00 per image</h2>
            <p className="text-lg text-gray-400 leading-relaxed">
              We&apos;ve made virtual staging affordable for everyone. We operate on high volume and low margins, so we
              offer the same low price to all customers. And there are no subscriptions — pay as you go.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-6 max-w-6xl mx-auto items-start">
            {/* Standard */}
            <div className="bg-[#13151F]/80 backdrop-blur-sm border border-white/[0.08] rounded-2xl p-8 hover:border-purple-500/30 transition-all duration-300">
              <div className="text-center mb-6">
                <h3 className="text-2xl font-bold text-white mb-3">Standard</h3>
                <div className="flex items-baseline justify-center gap-2">
                  <span className="text-5xl font-bold bg-gradient-to-r from-purple-400 to-blue-400 bg-clip-text text-transparent">$24</span>
                  <span className="text-gray-500">/image</span>
                </div>
                <p className="text-gray-500 text-sm mt-3">Perfect for individual rooms</p>
              </div>
              <ul className="space-y-3 mb-8">
                {['Photo-realistic staging', '48-hour turnaround', '8 style options', 'Unlimited revisions'].map((f) => (
                  <li key={f} className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-purple-400 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-400">{f}</span>
                  </li>
                ))}
              </ul>
              <Link href={signupUrl}>
                <button className="w-full py-3.5 bg-white/[0.05] border border-white/[0.12] text-white rounded-xl font-bold hover:bg-white/[0.1] transition-all">Get Started</button>
              </Link>
            </div>

            {/* Bulk - popular */}
            <div className="relative bg-gradient-to-b from-purple-600/20 to-[#13151F]/90 backdrop-blur-sm border-2 border-purple-500/40 rounded-2xl p-8 shadow-[0_30px_80px_rgba(124,58,237,0.25)] md:scale-105">
              <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 bg-gradient-to-r from-purple-500 to-blue-500 text-white px-5 py-1.5 rounded-full text-xs font-bold shadow-lg">MOST POPULAR</div>
              <div className="text-center mb-6">
                <h3 className="text-2xl font-bold text-white mb-3">Bulk Package</h3>
                <div className="flex items-baseline justify-center gap-2">
                  <span className="text-5xl font-bold text-white">$18</span>
                  <span className="text-gray-300">/image</span>
                </div>
                <p className="text-gray-300 text-sm mt-3">10+ images - Best value</p>
              </div>
              <ul className="space-y-3 mb-8">
                {['Everything in Standard', 'Priority processing', 'Dedicated editor', 'Volume discounts'].map((f) => (
                  <li key={f} className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-purple-300 flex-shrink-0 mt-0.5" />
                    <span className="text-white">{f}</span>
                  </li>
                ))}
              </ul>
              <Link href={signupUrl}>
                <button className="w-full py-3.5 bg-gradient-to-r from-purple-600 to-purple-500 hover:from-purple-500 hover:to-purple-400 text-white rounded-xl font-bold transition-all shadow-lg shadow-purple-500/30 hover:-translate-y-0.5">Start Project</button>
              </Link>
            </div>

            {/* Enterprise */}
            <div className="bg-[#13151F]/80 backdrop-blur-sm border border-white/[0.08] rounded-2xl p-8 hover:border-purple-500/30 transition-all duration-300">
              <div className="text-center mb-6">
                <h3 className="text-2xl font-bold text-white mb-3">Enterprise</h3>
                <div className="flex items-baseline justify-center gap-2">
                  <span className="text-5xl font-bold bg-gradient-to-r from-purple-400 to-blue-400 bg-clip-text text-transparent">Custom</span>
                </div>
                <p className="text-gray-500 text-sm mt-3">For agencies &amp; large teams</p>
              </div>
              <ul className="space-y-3 mb-8">
                {['Custom pricing plans', 'API integration', 'Dedicated account manager', 'White-label options'].map((f) => (
                  <li key={f} className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-purple-400 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-400">{f}</span>
                  </li>
                ))}
              </ul>
              <Link href="/contact">
                <button className="w-full py-3.5 bg-white/[0.05] border border-white/[0.12] text-white rounded-xl font-bold hover:bg-white/[0.1] transition-all">Contact Sales</button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ===================== PROCESS ===================== */}
      <section id="process" className="relative py-20 lg:py-28 px-6 lg:px-12 bg-white/[0.015] border-y border-white/[0.06] scroll-mt-20">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16 max-w-3xl mx-auto">
            <div className="mb-6"><Badge>LIGHTNING FAST 48 HOUR TURNAROUND</Badge></div>
            <h2 className="text-4xl lg:text-5xl font-bold leading-tight tracking-tight mb-4">Simple 4-step process</h2>
            <p className="text-lg text-gray-400 leading-relaxed">
              Need your images on the marketplace in a hurry? Our editors work around the clock, 24/7, and will
              virtually stage your images in under 48 hours.
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            {processSteps.map((step) => (
              <div key={step.n} className="bg-[#13151F]/80 backdrop-blur-sm border border-white/[0.08] rounded-2xl p-8 hover:border-purple-500/30 transition-all duration-300">
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-14 h-14 rounded-2xl bg-gradient-to-br from-purple-500 to-purple-600 flex items-center justify-center text-2xl font-bold text-white shadow-lg shadow-purple-500/30">
                    {step.n}
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-white mb-2">{step.title}</h3>
                    <p className="text-gray-400 leading-relaxed">{step.desc}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===================== FEATURES GRID ===================== */}
      <section className="relative py-20 lg:py-28 px-6 lg:px-12">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16 max-w-3xl mx-auto">
            <div className="mb-6"><Badge>UNLIMITED CHANGES AT NO EXTRA COST</Badge></div>
            <h2 className="text-4xl lg:text-5xl font-bold leading-tight tracking-tight mb-4">What makes our virtual staging different</h2>
            <p className="text-lg text-gray-400 leading-relaxed">
              Need to make a change to your staged image? Just request it within 2 months of the original job date
              and we&apos;ll do it at no extra cost.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {features.map((feature, i) => (
              <div key={i} className="group text-center bg-[#13151F]/80 backdrop-blur-sm border border-white/[0.08] rounded-2xl p-6 hover:border-purple-500/30 hover:-translate-y-1 transition-all duration-300">
                <div className="w-14 h-14 mx-auto rounded-2xl bg-gradient-to-br from-purple-500/20 to-blue-500/10 border border-purple-500/20 flex items-center justify-center text-2xl mb-4 group-hover:scale-110 transition-transform">{feature.icon}</div>
                <h3 className="font-bold text-lg text-white mb-2">{feature.title}</h3>
                <p className="text-sm text-gray-400 leading-relaxed">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===================== QUALITY GUARANTEE ===================== */}
      <section className="relative py-20 lg:py-28 px-6 lg:px-12 bg-white/[0.015] border-y border-white/[0.06]">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-12 items-center">
          <div className="space-y-6">
            <h2 className="text-4xl lg:text-5xl font-bold leading-tight tracking-tight">
              Quality{' '}
              <span className="bg-gradient-to-r from-purple-400 to-blue-400 bg-clip-text text-transparent">guaranteed.</span>
            </h2>
            <p className="text-lg text-gray-400 leading-relaxed">
              We stand behind our work with a 100% satisfaction guarantee. If you&apos;re not happy, we&apos;ll make it
              right or refund your money.
            </p>
            <ul className="space-y-4">
              {[
                '48-hour turnaround time',
                'Unlimited free revisions within 2 months',
                'Expert editors with 10+ years experience',
                'Photo-realistic quality guaranteed',
                '100% satisfaction guarantee',
                'Secure file handling & privacy',
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-3">
                  <Check className="w-6 h-6 text-purple-400 flex-shrink-0" />
                  <span className="text-gray-300 text-lg">{item}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="relative">
            <div className="absolute -inset-4 bg-purple-600/15 rounded-3xl blur-[80px] -z-10" />
            <div className="relative h-[500px] rounded-2xl overflow-hidden shadow-2xl border-2 border-white/[0.12]">
              <ProtectedAppImage
                src="/images/gallery/3vsaf.webp?auto=compress&cs=tinysrgb&w=1200"
                alt="Quality guarantee"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* ===================== FINAL CTA ===================== */}
      <section className="relative py-20 lg:py-28 px-6 lg:px-12 overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[900px] h-[900px] bg-purple-600/10 rounded-full blur-[160px] animate-glow-breathe" />
        </div>
        <div className="relative z-10 max-w-4xl mx-auto text-center">
          <div className="relative bg-[#13151F]/95 backdrop-blur-2xl rounded-3xl border-2 border-white/[0.12] shadow-[0_30px_80px_rgba(0,0,0,0.6)] p-12 lg:p-16">
            <div className="absolute -inset-4 bg-purple-600/15 rounded-3xl blur-[80px] -z-10" />
            <h2 className="text-4xl lg:text-5xl font-bold leading-tight tracking-tight mb-5">
              Let buyers fall in love with your{' '}
              <span className="bg-gradient-to-r from-purple-400 to-blue-400 bg-clip-text text-transparent">space.</span>
            </h2>
            <p className="text-lg text-gray-400 leading-relaxed mb-10 max-w-2xl mx-auto">
              Virtual staging removes the barriers between an empty space and a buyer&apos;s imagination. Transform your
              listings today and watch them sell faster at higher prices.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link href={signupUrl}>
                <button className="px-10 py-4 bg-gradient-to-r from-purple-600 to-purple-500 hover:from-purple-500 hover:to-purple-400 text-white font-bold text-lg rounded-xl transition-all duration-300 shadow-xl shadow-purple-500/30 hover:-translate-y-0.5">
                  Start Staging Now
                </button>
              </Link>
              <Link href="/homepage#pricing">
                <button className="px-10 py-4 bg-white/[0.03] hover:bg-white/[0.08] backdrop-blur-sm text-white font-bold text-lg rounded-xl transition-all duration-300 border border-white/[0.15] hover:border-white/30">
                  View Pricing
                </button>
              </Link>
            </div>
            <p className="text-sm text-gray-500 mt-6">
              No credit card required to create an account • 48-hour turnaround • Unlimited revisions
            </p>
          </div>
        </div>
      </section>

      <Footer />

      {/* Animations */}
      <style jsx global>{`
        @keyframes glow-breathe {
          0%, 100% { opacity: 0.1; transform: scale(1); }
          50% { opacity: 0.15; transform: scale(1.05); }
        }
        @keyframes glow-breathe-delayed {
          0%, 100% { opacity: 0.05; transform: scale(1); }
          50% { opacity: 0.08; transform: scale(1.03); }
        }
        .animate-glow-breathe { animation: glow-breathe 8s ease-in-out infinite; }
        .animate-glow-breathe-delayed { animation: glow-breathe-delayed 10s ease-in-out infinite; animation-delay: 2s; }
      `}</style>
    </div>
  );
}
